'use client';

import { useEffect, useRef } from 'react';

type CallToActionBackdropVideoProps = {
    src: string;
    type?: string;
    poster?: string;
};

/**
 * The closing block's backdrop as a loop. The block sits at the foot of almost
 * every page, so the file loads nothing until the section comes into view, and
 * the loop pauses again once it has left. Under reduced motion the poster
 * stays.
 */
export function CallToActionBackdropVideo({ src, type, poster }: CallToActionBackdropVideoProps) {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    void video.play().catch(() => {
                        // Autoplay can be refused (battery saver, iOS low-power);
                        // the poster frame stays, which is the same picture.
                    });
                } else {
                    video.pause();
                }
            },
            { rootMargin: '200px 0px' },
        );

        observer.observe(video);

        return () => observer.disconnect();
    }, []);

    return (
        <video
            ref={videoRef}
            className="cta-section__backdrop"
            muted
            loop
            playsInline
            preload="none"
            poster={poster}
            aria-hidden="true"
        >
            <source src={src} type={type} />
        </video>
    );
}
